import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { MessageCircle, Palette, CheckCircle, Hammer, Lightbulb, Building, Store, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  MessageCircle,
  Palette,
  CheckCircle,
  Hammer,
  Lightbulb,
  Building,
  Store,
  Users
};

const Process = () => {
  const [processSteps, setProcessSteps] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Buscar dados da API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [processResponse, servicesResponse] = await Promise.all([
          fetch(`${API}/processo`),
          fetch(`${API}/servicos`)
        ]);

        if (processResponse.ok && servicesResponse.ok) {
          const processData = await processResponse.json();
          const servicesData = await servicesResponse.json();
          
          setProcessSteps(processData);
          setServices(servicesData);
        } else {
          // Fallback para dados mock
          const { processSteps: mockProcess, services: mockServices } = await import('../data/mockData');
          setProcessSteps(mockProcess);
          setServices(mockServices);
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        // Fallback para dados mock
        const { processSteps: mockProcess, services: mockServices } = await import('../data/mockData');
        setProcessSteps(mockProcess);
        setServices(mockServices);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <section id="processo" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF1616]"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="processo" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Process Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">
            NOSSO{' '}
            <span className="text-[#FF1616]">
              PROCESSO
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-medium">
            Metodologia comprovada que garante resultados excepcionais do briefing à entrega
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {processSteps.map((step, index) => {
            const IconComponent = iconMap[step.icon];
            return (
              <div key={step.id} className="relative">
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-gray-50 to-white group">
                  <CardContent className="p-6 text-center">
                    {/* Step Number */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-[#FF1616] text-white rounded-full flex items-center justify-center font-black text-sm">
                      {step.id}
                    </div>
                    
                    {/* Icon */}
                    <div className="w-16 h-16 bg-gradient-to-r from-red-50 to-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-[#FF1616]" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 tracking-wide">
                      {step.title}
                    </h3>
                    
                    <p className="text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </CardContent>
                </Card>
                
                {/* Connection Arrow */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-red-300 to-red-400 z-10">
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-[#FF1616] rounded-full"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Services Grid */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 tracking-wide">
              NOSSOS SERVIÇOS
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto font-medium">
              Soluções completas para transformar sua visão em experiências inesquecíveis
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 border-0 bg-white group">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#FF1616] rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    
                    <h4 className="text-lg font-bold text-gray-900 mb-3 tracking-wide">
                      {service.title}
                    </h4>
                    
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Process Images */}
        <div className="mt-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div key={`img-${step.id}`} className="relative group overflow-hidden rounded-2xl">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="text-sm font-bold tracking-wide">{step.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;